import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OriginBadge } from "@/components/OriginBadge";
import { Plus, CheckCircle2, Pencil, Trash2, CreditCard, Link2Off, Search, X, Send, RefreshCw, ShieldCheck, Undo2, FastForward, RotateCcw, Archive, AlertTriangle, ReceiptText, GraduationCap, Check } from "lucide-react";
import { formatDMY, todayISO, daysAgoISO, currentMonthISO } from "@/lib/dateFormat";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { BillingAnomalies } from "@/components/BillingAnomalies";
import { AdvancesPanel } from "@/components/AdvancesPanel";
import { CreditNoteDialog } from "@/components/CreditNoteDialog";

const METHOD_OPTIONS = [
  { key: "cash", label: "Efectivo" },
  { key: "deposit", label: "Depósito" },
  { key: "card", label: "Tarjeta" },
  { key: "unspecified", label: "Sin especificar" },
];
const methodLabel = (m) => METHOD_OPTIONS.find((o) => o.key === m)?.label || "—";

export default function Payments() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [recalcing, setRecalcing] = useState(false);
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    student_id: "", program_id: "", amount: 0, status: "pending",
    due_date: "", paid_date: "", sale_date: "", notes: "", payment_method: "unspecified",
  });
  // Modal del ✓: pide forma de pago y CUÁNDO se pagó (la plata no entra
  // automática, así que se puede registrar uno o dos días después).
  const [payMethodDialog, setPayMethodDialog] = useState({ open: false, id: null, method: "cash", paidDate: "", issued: null });
  // Orphan-only filter: shows exclusively payments whose enrollment no
  // longer exists in the DB (returned by backend as enrollment_orphaned=true).
  const [orphansOnly, setOrphansOnly] = useState(false);
  // Cobro sin programa que el admin decide conservar (el ingreso fue real).
  const [keepTarget, setKeepTarget] = useState(null);
  const [creditNoteTarget, setCreditNoteTarget] = useState(null);
  const [keepReason, setKeepReason] = useState("");
  // "Cerrar ciclo ahora": el cierre normal ocurre a medianoche (hora CR).
  const [closeTarget, setCloseTarget] = useState(null);
  const [closing, setClosing] = useState(false);
  // "Reabrir ciclo": el ciclo se cerró y facturó, pero una corrección posterior
  // lo dejó por debajo de su tope. Reabrirlo NO depende de borrar la factura.
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopening, setReopening] = useState(false);
  // Los resúmenes de PH y BEC se pueden dar por revisados con el check: se
  // guarda la firma (cuántos y cuánto) y el cuadro vuelve solo si eso cambia.
  const [seenBanners, setSeenBanners] = useState(() => {
    try { return JSON.parse(localStorage.getItem("payments-banners-seen") || "{}"); }
    catch { return {}; }
  });
  const markBannerSeen = (key, sig) => {
    setSeenBanners((prev) => {
      const next = { ...prev, [key]: sig };
      localStorage.setItem("payments-banners-seen", JSON.stringify(next));
      return next;
    });
  };
  // Recorte por mes: la pantalla ya no trae el historial completo.
  const [month, setMonth] = useState(() => currentMonthISO());
  const [dateMode, setDateMode] = useState("emision"); // emision | pago
  const [statusFilter, setStatusFilter] = useState("all");
  const monthOptions = useMemo(() => {
    const out = [];
    // Arranca en el mes de COSTA RICA, no en el del navegador ni en UTC.
    const [y0, m0] = currentMonthISO().split("-").map(Number);
    const d = new Date(y0, m0 - 1, 1);
    for (let i = 0; i < 24; i += 1) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      out.push({ key, label: d.toLocaleDateString("es-CR", { month: "long", year: "numeric" }) });
      d.setMonth(d.getMonth() - 1);
    }
    return out;
  }, []);
  const [studentQuery, setStudentQuery] = useState("");
  const [pickedStudent, setPickedStudent] = useState(null);   // { user_id, name }
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Ciclos ya cobrados con el contador por debajo de su plan (huella del bug
  // del 13/09/2026: cambiar las sesiones reescribía el ciclo ya pagado).
  const [suspects, setSuspects] = useState([]);
  const [suspectsOpen, setSuspectsOpen] = useState(false);
  const [fixDraft, setFixDraft] = useState({});   // payment_id → {included, consumed}
  const [fixing, setFixing] = useState(null);

  const loadSuspects = async () => {
    if (!isSuper) return;
    try {
      const { data } = await api.get("/billing/counter-suspects");
      setSuspects(data.items || []);
    } catch (e) { /* diagnóstico opcional: si falla, la tabla sigue igual */ }
  };

  const fixCounter = async (row) => {
    const draft = fixDraft[row.payment_id] || {};
    const included = Number(draft.included ?? row.program_classes_included ?? row.classes_included);
    const consumed = Number(draft.consumed ?? included);
    setFixing(row.payment_id);
    try {
      await api.post(`/payments/${row.payment_id}/fix-cycle-counter`, {
        classes_included: included, classes_consumed: consumed,
      });
      toast.success(`Contador corregido a ${consumed}/${included}`);
      await Promise.all([load(), loadSuspects()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setFixing(null);
    }
  };

  const load = async () => {
    const qs = new URLSearchParams();
    if (pickedStudent) {
      // Al elegir un estudiante se busca en TODO su historial, no solo en el mes.
      qs.set("student_id", pickedStudent.user_id);
    } else if (month !== "all") {
      qs.set("month", month);
      qs.set("by", dateMode);
    }
    if (["pending", "overdue", "paid", "cancelled"].includes(statusFilter)) qs.set("status", statusFilter);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const [p, u, pr] = await Promise.all([
      api.get(`/payments${suffix}`), api.get("/users"), api.get("/programs"),
    ]);
    setPayments(p.data);
    setStudents(u.data.filter((x) => x.role === "student"));
    setPrograms(pr.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, dateMode, statusFilter, pickedStudent]);
  useEffect(() => { loadSuspects(); /* eslint-disable-next-line */ }, [isSuper]);

  // Type-ahead: suggests students whose name (or email) contains what you typed.
  const suggestions = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    if (!q || pickedStudent) return [];
    return students
      .filter((s) => (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [studentQuery, students, pickedStudent]);

  // El aviso de contadores raros es de toda la escuela, así que al buscar a
  // alguien se recorta a esa persona: si no es suya, no tiene por qué salirle.
  const visibleSuspects = useMemo(() => {
    if (pickedStudent) return suspects.filter((r) => r.student_id === pickedStudent.user_id);
    const q = studentQuery.trim().toLowerCase();
    if (!q) return suspects;
    return suspects.filter((r) => (r.student_name || "").toLowerCase().includes(q));
  }, [suspects, pickedStudent, studentQuery]);

  const studentsMap = useMemo(() => Object.fromEntries(students.map((s) => [s.user_id, s])), [students]);
  const programsMap = useMemo(() => Object.fromEntries(programs.map((p) => [p.id, p])), [programs]);

  const openCreate = () => {
    setEditing(null);
    setForm({ student_id: "", program_id: "", amount: 0, status: "pending", due_date: "", paid_date: "", sale_date: "", notes: "", classes_included: 0, payment_method: "unspecified" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      student_id: p.student_id, program_id: p.program_id || "", amount: p.amount,
      status: p.status, due_date: p.due_date || "", paid_date: p.paid_date || "",
      sale_date: p.sale_date || "", notes: p.notes || "",
      classes_included: p.classes_included || 0,
      payment_method: p.payment_method || (p.status === "paid" ? "card" : "unspecified"),
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.student_id || !form.amount) { toast.error(t("error_generic")); return; }
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        classes_included: Number(form.classes_included),
        program_id: form.program_id || null,
        due_date: form.due_date || null,
        paid_date: form.paid_date || null,
        sale_date: form.sale_date || null,
        // Only send payment_method when marking as paid — otherwise clear it
        payment_method: form.status === "paid" ? (form.payment_method || "unspecified") : null,
      };
      if (editing) await api.put(`/payments/${editing.id}`, payload);
      else await api.post("/payments", payload);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch { toast.error(t("error_generic")); }
  };

  const openMarkPaid = (p) => setPayMethodDialog({
    open: true, id: p.id, method: "cash", paidDate: todayISO(),
    issued: p.invoice_issued_at ? String(p.invoice_issued_at).slice(0, 10) : null,
  });
  const closeMarkPaid = () => setPayMethodDialog({ open: false, id: null, method: "cash", paidDate: "", issued: null });
  const confirmMarkPaid = async () => {
    if (payMethodDialog.paidDate > todayISO()) {
      toast.error("La fecha de pago no puede ser futura");
      return;
    }
    try {
      await api.put(`/payments/${payMethodDialog.id}`, {
        status: "paid",
        payment_method: payMethodDialog.method,
        paid_date: payMethodDialog.paidDate || todayISO(),
      });
      toast.success(t("saved"));
      closeMarkPaid();
      load();
    } catch {
      toast.error(t("error_generic"));
    }
  };

  // "Solo históricos (PH)" y "Solo becas (BEC)" viven dentro del filtro Estado:
  // son tipos de cobro, no estados, y se recortan aquí en pantalla.
  const historicalOnly = statusFilter === "historical";
  const scholarshipOnly = statusFilter === "scholarship";

  const visiblePayments = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    return payments.filter((p) => {
      if (orphansOnly && !p.enrollment_orphaned) return false;
      // Solo históricos (PH): plata recibida antes de la plataforma.
      if (historicalOnly && (!p.historical_number || p.scholarship)) return false;
      // Solo becas (BEC): el ciclo se dio gratis.
      if (scholarshipOnly && !p.scholarship) return false;
      if (pickedStudent) return p.student_id === pickedStudent.user_id;
      if (!q) return true;
      const name = (studentsMap[p.student_id]?.name || "").toLowerCase();
      return name.includes(q);
    });
  }, [payments, orphansOnly, historicalOnly, scholarshipOnly, pickedStudent, studentQuery, studentsMap]);

  const totals = useMemo(() => {
    const paid = visiblePayments.filter((p) => p.status === "paid")
      .reduce((a, p) => a + Number(p.amount || 0), 0);
    const pending = visiblePayments.filter((p) => p.status !== "paid")
      .reduce((a, p) => a + Number(p.amount || 0), 0);
    return { paid, pending };
  }, [visiblePayments]);

  const sendInvoice = async (p) => {
    if (!window.confirm(`¿Enviarle al estudiante el recordatorio de la factura ${p.invoice_number || ""} para que la pague?`)) return;
    try {
      await api.post(`/payments/${p.id}/send-invoice`);
      toast.success("Recordatorio de factura enviado al estudiante");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    }
  };

  // Recalcula "clases tomadas" de todos los ciclos desde las asistencias
  // (única fuente de verdad) y opcionalmente reabre los que se cerraron antes
  // de tiempo, eliminando el cobro del ciclo siguiente creado por error.
  const recalcCycles = async () => {
    setRecalcing(true);
    try {
      const dry = (await api.post("/billing/recompute-cycles?apply=false")).data;
      const lines = (dry.drifted || []).slice(0, 15)
        .map((r) => `• ${r.student_name || r.student_id} · ciclo ${r.cycle_index}: ${r.stored} → ${r.derived}/${r.classes_included}`)
        .join("\n");
      const prem = (dry.premature_closures || []).slice(0, 15)
        .map((r) => `• ${r.student_name || r.student_id} · ciclo ${r.cycle_index}: cerrado en ${r.derived}/${r.classes_included}`)
        .join("\n");
      const dups = (dry.duplicate_cycles || []).slice(0, 15)
        .map((g) => `• ${g.student_name || g.student_id} · ciclo ${g.cycle_index}: se conserva ${g.keep.invoice_number || g.keep.payment_id}, se elimina ${g.extras.map((e) => e.invoice_number || e.payment_id).join(", ")}`)
        .join("\n");
      const born = (dry.born_at_cap || []).slice(0, 15)
        .map((r) => `• ${r.student_name || r.student_id} · ciclo ${r.cycle_index} creado el ${r.created_on} con las ${r.classes_included} clases ya dadas (última: ${r.last_class_of_cycle}) → ${r.safe_to_fix ? `pasaría al ciclo ${r.suggested_cycle_index}` : "ya está pagado, solo se reporta"}`)
        .join("\n");
      if (!dry.drifted_count && !dry.premature_count && !dry.duplicate_count && !dry.born_at_cap_count) {
        toast.success("Todos los ciclos están correctos, nada que corregir");
        return;
      }
      const msg = `Ciclos desfasados: ${dry.drifted_count}\n${lines}\n\n` +
        `Ciclos cerrados antes de tiempo: ${dry.premature_count}\n${prem}\n\n` +
        `Cobros dobles del mismo ciclo: ${dry.duplicate_count}\n${dups}\n\n` +
        `Cobros que nacieron con el ciclo ya consumido: ${dry.born_at_cap_count}\n${born}\n\n` +
        `¿Aplicar la corrección? (se recalculan las clases tomadas, se corren al ciclo siguiente los cobros que nacieron al tope, se reabren los ciclos cerrados por error y se eliminan los cobros dobles, anulando su factura)`;
      if (!window.confirm(msg)) return;
      const r = (await api.post("/billing/recompute-cycles?apply=true&reopen=true&dedupe=true&reindex=true")).data;
      toast.success(`Corregidos ${r.drifted_count} ciclos · ${(r.reindexed || []).length} corridos de ciclo · ${(r.reopened || []).length} reabiertos · ${(r.deduped || []).length} cobros dobles eliminados`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo recalcular");
    } finally {
      setRecalcing(false);
    }
  };

  const remove = async (id) => {
    if (!confirm(t("confirm_delete"))) return;
    await api.delete(`/payments/${id}`);
    load();
  };

  const keepOrphan = async () => {
    try {
      await api.post(`/payments/${keepTarget.id}/keep-orphan`, { reason: keepReason });
      toast.success(t("kept_ok"));
      setKeepTarget(null); setKeepReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const unkeepOrphan = async (p) => {    try {
      await api.delete(`/payments/${p.id}/keep-orphan`);
      toast.success(t("unkept_ok"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const closeCycleNow = async () => {
    if (!closeTarget) return;
    setClosing(true);
    try {
      await api.post(`/payments/${closeTarget.id}/close-cycle`);
      toast.success("Ciclo cerrado. Se creó el cobro del ciclo siguiente y se envió el recordatorio de renovación.");
      setCloseTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setClosing(false);
    }
  };

  const reopenCycle = async () => {
    if (!reopenTarget) return;
    setReopening(true);
    try {
      const r = await api.post(`/payments/${reopenTarget.id}/reopen-cycle`);
      const nxt = r.data?.next_cycle_payment;
      toast.success(
        `Ciclo reabierto en ${r.data.classes_consumed}/${r.data.classes_included}.`
        + (nxt ? ` La factura del ciclo siguiente (${nxt.invoice_number || "pendiente"}) sigue ahí: borrala si salió antes de tiempo.` : "")
      );
      setReopenTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally {
      setReopening(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("payments")}
        testId="payments-header"
        action={
          <div className="flex items-center gap-2">
            {isSuper && (
              <Button variant="outline" onClick={recalcCycles} disabled={recalcing}
                      data-testid="recalc-cycles-button"
                      title="Recalcula las clases tomadas de cada ciclo desde las asistencias">
                <RefreshCw className={`h-4 w-4 ${recalcing ? "animate-spin" : ""}`} /> Recalcular ciclos
              </Button>
            )}
            <Button data-testid="new-payment-button" onClick={openCreate}><Plus className="h-4 w-4" /> {t("new_payment")}</Button>
          </div>
        }
      />
      <BillingAnomalies onFixed={load} />
      {isSuper && <AdvancesPanel onApplied={load} />}
      <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="payments-search-bar">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="payments-student-search"
            className="pl-9 pr-9"
            placeholder="Buscar estudiante por nombre…"
            value={pickedStudent ? pickedStudent.name : studentQuery}
            onChange={(e) => { setPickedStudent(null); setStudentQuery(e.target.value); setShowSuggestions(true); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          />
          {(pickedStudent || studentQuery) && (
            <button data-testid="payments-search-clear"
                    onClick={() => { setPickedStudent(null); setStudentQuery(""); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 grid place-items-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {showSuggestions && suggestions.length > 0 && (
            <ul className="absolute z-30 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden"
                data-testid="payments-search-suggestions">
              {suggestions.map((s) => (
                <li key={s.user_id}>
                  <button
                    data-testid={`payments-suggestion-${s.user_id}`}
                    onMouseDown={() => { setPickedStudent({ user_id: s.user_id, name: s.name }); setStudentQuery(s.name || ""); setShowSuggestions(false); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                    <span className="text-slate-800">{s.name}</span>
                    {s.email && <span className="text-slate-400 text-xs ml-2">{s.email}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {(pickedStudent || studentQuery) && (
          <p className="text-sm text-slate-600" data-testid="payments-search-summary">
            <strong>{visiblePayments.length}</strong> {visiblePayments.length === 1 ? "factura" : "facturas"}
            {" · "}Pagado: <strong>${totals.paid.toFixed(2)}</strong>
            {" · "}Pendiente: <strong className="text-orange-600">${totals.pending.toFixed(2)}</strong>
          </p>
        )}
        <div className="flex flex-wrap items-end gap-3 mt-3">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1">Mes</label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[190px] bg-white" data-testid="payments-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
                <SelectItem value="all">Todo el historial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {month !== "all" && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Contar el mes por</label>
              <Select value={dateMode} onValueChange={setDateMode}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="payments-datemode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="emision">Fecha de emisión</SelectItem>
                  <SelectItem value="venta">Fecha de venta</SelectItem>
                  <SelectItem value="pago">Fecha de pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 mb-1">Estado</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[190px] bg-white" data-testid="payments-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="overdue">Vencidas</SelectItem>
                <SelectItem value="paid">Pagadas</SelectItem>
                <SelectItem value="cancelled">Anuladas</SelectItem>
                <SelectItem value="scholarship" data-testid="payments-filter-scholarship">Solo becas (BEC)</SelectItem>
                <SelectItem value="historical" data-testid="payments-filter-historical">Solo históricos (PH)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-slate-700 pb-2" data-testid="payments-period-summary">
            <strong>{visiblePayments.length}</strong> {visiblePayments.length === 1 ? "factura" : "facturas"}
            {" · "}Pagado: <strong className="text-emerald-700">${totals.paid.toFixed(2)}</strong>
            {" · "}Pendiente: <strong className="text-orange-600">${totals.pending.toFixed(2)}</strong>
          </p>
        </div>
      </div>

      {/* Ciclos cerrados que quedaron por debajo de su tope: la señal de que la
          factura del ciclo siguiente pudo salir antes de tiempo. */}
      {isSuper && (() => {
        const flagged = payments.filter((p) => p.cycle_closed_below_top);
        if (flagged.length === 0) return null;
        return (
          <div className="mb-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm" data-testid="reopen-toolbar">
            <div className="flex items-start gap-3">
              <RotateCcw className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-900">
                  <strong>{flagged.length}</strong> {flagged.length === 1 ? "ciclo cerrado quedó" : "ciclos cerrados quedaron"} por
                  debajo de su tope después de corregir asistencias. Puede que su factura del ciclo
                  siguiente haya salido antes de tiempo.
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  {flagged.map((p) => `${p.primary_student_name || p.student_id} (${p.classes_consumed || 0}/${p.classes_included})`).join(" · ")}
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  Revisalos abajo: con <strong>Reabrir ciclo</strong> el ciclo vuelve a estar activo y se
                  cierra otra vez, con factura nueva, cuando de verdad llegue a su tope.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Facturas de renovación emitidas antes de tiempo: el ciclo anterior se
          reabrió solo porque una corrección lo dejó por debajo de su tope. */}
      {(() => {
        const early = payments.filter((p) => p.premature_invoice);
        if (early.length === 0) return null;
        return (
          <div className="mb-3 p-3 rounded-lg border border-red-300 bg-red-50 text-sm" data-testid="premature-toolbar">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-900">
                  <strong>{early.length}</strong> {early.length === 1 ? "factura de renovación salió" : "facturas de renovación salieron"} antes
                  de tiempo. El ciclo anterior ya se <strong>reabrió solo</strong> y volverá a cerrarse
                  cuando de verdad llegue a su tope.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {early.map((p) => (
                    <Button key={p.id} size="sm" variant="outline" className="border-red-400 text-red-900"
                            data-testid={`premature-delete-${p.id}`}
                            onClick={() => remove(p.id)}>
                      Borrar {p.invoice_number || "sin número"} · {p.primary_student_name || p.student_id}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-red-800 mt-2">
                  Al borrarla, su consecutivo queda anulado (no se reutiliza) y el estudiante deja de
                  verla en su portal.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Ciclos ya cobrados cuyo contador quedó por debajo de su plan. */}
      {isSuper && visibleSuspects.length > 0 && (
        <div className="mb-3 p-3 rounded-lg border border-amber-300 bg-amber-50 text-sm" data-testid="counter-suspects-toolbar">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-900">
                <strong>{visibleSuspects.length}</strong> {visibleSuspects.length === 1 ? "ciclo ya cobrado tiene" : "ciclos ya cobrados tienen"} el
                contador por debajo de su plan (por ejemplo 0/4 en un ciclo que se completó).
              </p>
              <p className="text-xs text-amber-800 mt-1" data-testid="counter-suspects-who">
                {(() => {
                  const names = [...new Set(visibleSuspects.map((r) => r.student_name || r.student_id))];
                  const shown = names.slice(0, 3).join(", ");
                  return `De: ${shown}${names.length > 3 ? ` y ${names.length - 3} más` : ""}.`;
                })()}
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Pasa cuando el programa se borró o cuando se le cambiaron las sesiones por ciclo. Ya no
                puede volver a ocurrir, pero los que quedaron mal hay que corregirlos a mano.
              </p>
              <Button size="sm" variant="outline" className="mt-2 border-amber-400 text-amber-900"
                      onClick={() => setSuspectsOpen(true)} data-testid="counter-suspects-open">
                Revisarlos ({visibleSuspects.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pagos históricos (PH): plata recibida antes de la plataforma.
          Las becas van aparte: son plata que la escuela regala, no plata vieja. */}
      {(() => {
        const hist = payments.filter((p) => p.historical_number && !p.scholarship);
        if (hist.length === 0) return null;
        const total = hist.reduce((a, p) => a + Number(p.amount || 0), 0);
        const sig = `${hist.length}:${total.toFixed(2)}`;
        if (seenBanners.historical === sig && !historicalOnly) return null;
        return (
          <div className="mb-3 flex flex-wrap items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 text-sm" data-testid="historical-toolbar">
            <Archive className="h-4 w-4 text-slate-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-slate-800">{hist.length} pago{hist.length === 1 ? "" : "s"} histórico{hist.length === 1 ? "" : "s"} (PH)</span>
              <span className="text-slate-600 ml-2">
                por $ {total.toFixed(2)} — plata recibida antes de la plataforma: no suma en los
                ingresos ni consume consecutivo fiscal.
              </span>
            </div>
            <Button
              size="sm"
              variant={historicalOnly ? "default" : "outline"}
              onClick={() => setStatusFilter(historicalOnly ? "all" : "historical")}
              data-testid="toggle-historical-only"
            >
              {historicalOnly ? "Ver todos" : "Solo históricos (PH)"}
            </Button>
            <Button
              size="sm" variant="ghost" className="text-slate-600"
              title="Dar por revisado: el cuadro se esconde y solo vuelve si cambia"
              onClick={() => markBannerSeen("historical", sig)}
              data-testid="dismiss-historical-toolbar"
            >
              <Check className="h-4 w-4 mr-1" /> Revisado
            </Button>
          </div>
        );
      })()}

      {/* Becas (BEC): el ciclo se dio gratis. Es inversión de la escuela, así que
          se resume aparte y no se mezcla con la plata vieja (29/10/2026). */}
      {(() => {
        const becas = payments.filter((p) => p.scholarship);
        if (becas.length === 0) return null;
        const regalado = becas.reduce(
          (a, p) => a + Number(p.scholarship_value || 0), 0);
        const sig = `${becas.length}:${regalado.toFixed(2)}`;
        if (seenBanners.scholarship === sig && !scholarshipOnly) return null;
        return (
          <div className="mb-3 flex flex-wrap items-center gap-3 p-3 rounded-lg border border-violet-200 bg-violet-50 text-sm" data-testid="scholarship-toolbar">
            <GraduationCap className="h-4 w-4 text-violet-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-violet-900">{becas.length} beca{becas.length === 1 ? "" : "s"} (BEC)</span>
              <span className="text-violet-800 ml-2">
                por $ {regalado.toFixed(2)} regalados — inversión de la escuela: el ciclo se dio
                gratis, no es venta ni plata pendiente.
              </span>
            </div>
            <Button
              size="sm"
              variant={scholarshipOnly ? "default" : "outline"}
              className={scholarshipOnly ? "" : "border-violet-300 text-violet-800"}
              onClick={() => setStatusFilter(scholarshipOnly ? "all" : "scholarship")}
              data-testid="toggle-scholarship-only"
            >
              {scholarshipOnly ? "Ver todos" : "Solo becas (BEC)"}
            </Button>
            <Button
              size="sm" variant="ghost" className="text-violet-700"
              title="Dar por revisado: el cuadro se esconde y solo vuelve si cambia"
              onClick={() => markBannerSeen("scholarship", sig)}
              data-testid="dismiss-scholarship-toolbar"
            >
              <Check className="h-4 w-4 mr-1" /> Revisado
            </Button>
          </div>
        );
      })()}

      {/* Orphan-payment toolbar: appears only when at least one orphan exists,
          so a clean dataset stays uncluttered. */}
      {(() => {
        const orphanCount = payments.filter((p) => p.enrollment_orphaned).length;
        if (orphanCount === 0) return null;
        return (
          <div className="mb-3 flex items-center gap-3 p-3 rounded-lg border border-red-200 bg-red-50 text-sm" data-testid="orphan-toolbar">
            <Link2Off className="h-4 w-4 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-semibold text-red-800">{orphanCount} {orphanCount === 1 ? t("orphan_payment_one") : t("orphan_payments_many")}</span>
              <span className="text-red-700 ml-2">{t("orphan_payments_hint")}</span>
            </div>
            <Button
              size="sm"
              variant={orphansOnly ? "default" : "outline"}
              onClick={() => setOrphansOnly((v) => !v)}
              data-testid="toggle-orphans-only"
              className={orphansOnly ? "bg-red-600 hover:bg-red-700" : "border-red-300 text-red-700"}
            >
              {orphansOnly ? <><X className="h-3.5 w-3.5" /> {t("show_all_payments")}</> : t("show_orphans_only")}
            </Button>
          </div>
        );
      })()}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("invoice_number_short")}</TableHead>
              <TableHead>{t("student")}</TableHead>
              <TableHead>Etiqueta</TableHead>
              <TableHead>{t("classes_progress")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("amount")}</TableHead>
              <TableHead>Forma de pago</TableHead>
              <TableHead>{t("issued_date")}</TableHead>
              <TableHead>Días vencidos</TableHead>
              <TableHead>Venta</TableHead>
              <TableHead>{t("paid_date")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visiblePayments.length === 0 && (
              <TableRow><TableCell colSpan={12} className="text-center text-slate-500 py-10">{t("no_data")}</TableCell></TableRow>
            )}
            {visiblePayments.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.id}`} className={p.enrollment_orphaned ? "bg-red-50/60 hover:bg-red-50" : ""}>
                <TableCell className="font-mono text-xs whitespace-nowrap" data-testid={`payment-invoice-${p.id}`}>
                  {p.invoice_number ? (
                    <span className={p.is_annulled ? "line-through text-slate-400" : "text-slate-900 font-semibold"}
                          title={p.invoice_number?.startsWith("PH-")
                            ? "Pago histórico: recibido antes de la plataforma, no consume consecutivo fiscal"
                            : undefined}>
                      {p.invoice_number}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {studentsMap[p.student_id]?.name || p.student_id}
                    {p.enrollment_orphaned && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-red-100 text-red-700 border border-red-300"
                        title={t("orphan_badge_tooltip")}
                        data-testid={`orphan-badge-${p.id}`}
                      >
                        <Link2Off className="h-2.5 w-2.5" /> {t("orphan_badge")}
                      </span>
                    )}
                    {p.orphan_kept && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-300"
                        title={p.orphan_kept_reason
                          ? `${t("kept_badge_tooltip")} — ${p.orphan_kept_reason}`
                          : t("kept_badge_tooltip")}
                        data-testid={`kept-badge-${p.id}`}
                      >
                        <ShieldCheck className="h-2.5 w-2.5" /> {t("kept_badge")}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell data-testid={`payment-origin-${p.id}`}>
                  <OriginBadge payment={p} />
                </TableCell>
                <TableCell>
                  {p.classes_included > 0 ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono">
                        {p.enrollment_missing
                          ? <span className="text-slate-400" title={t("progress_no_program")}>—</span>
                          : `${p.classes_consumed || 0}/${p.classes_included}`}
                      </span>
                      {(() => {
                        const at = p.invoice_doc_sent_at;
                        if (at) {
                          const what = "Recordatorio de factura por pagar";
                          return (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200"
                                  title={`${what} · enviada al estudiante el ${formatDMY(at.slice(0, 10), lang)}`}
                                  data-testid={`invoice-sent-${p.id}`}>
                              Enviada {formatDMY(at.slice(0, 10), lang)}
                            </span>
                          );
                        }
                        if (p.invoice_sent) {
                          if (p.cycle_closed_below_top) {
                            return (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-300"
                                    title={`El ciclo se cerró y facturó, pero después se corrigieron asistencias y quedó en ${p.classes_consumed || 0}/${p.classes_included}. Revisá si la factura del ciclo siguiente salió antes de tiempo: podés reabrir este ciclo.`}
                                    data-testid={`cycle-below-top-${p.id}`}>
                                Cerrado con {p.classes_consumed || 0}/{p.classes_included} · revisar
                              </span>
                            );
                          }
                          return (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-slate-50 text-slate-500 border border-slate-200"
                                  title="El ciclo cerró. La factura está disponible para el estudiante en su portal (Mis pagos)."
                                  data-testid={`invoice-cycle-closed-${p.id}`}>
                              Ciclo cerrado
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className={p.credit_note_total > 0 ? "line-through text-slate-400" : ""}>
                      $ {Number(p.amount).toFixed(2)} <span className="text-[10px] text-slate-500">USD</span>
                    </span>
                    {p.credit_note_total > 0 && (
                      <span className="text-[10px] text-rose-700 font-semibold"
                            title={`Nota(s) de crédito ${(p.credit_note_numbers || []).join(", ")} por $${Number(p.credit_note_total).toFixed(2)}`}
                            data-testid={`credit-note-badge-${p.id}`}>
                        NC −${Number(p.credit_note_total).toFixed(2)} · neto ${Number(p.net_amount).toFixed(2)}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {p.status === "paid" ? (
                    <span className={p.payment_method === "unspecified" ? "italic text-slate-400" : "text-slate-700"} data-testid={`payment-method-${p.id}`}>
                      {methodLabel(p.payment_method || "unspecified")}
                    </span>
                  ) : <span className="text-slate-300">—</span>}
                </TableCell>
                <TableCell data-testid={`payment-issued-${p.id}`}>
                  {p.invoice_issued_at
                    ? formatDMY(String(p.invoice_issued_at).slice(0, 10), lang)
                    : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell data-testid={`payment-overdue-${p.id}`}>
                  {p.days_overdue > 0 ? (
                    <span className="text-red-600 font-semibold text-xs"
                          title={p.status === "paid"
                            ? `Se pagó con ${p.days_overdue} días de atraso`
                            : `Vencida hace ${p.days_overdue} días`}>
                      {p.days_overdue}
                    </span>
                  ) : (p.is_overdue && p.status !== "paid") ? (
                    <span className="text-red-600 font-semibold text-xs">Vencida hoy</span>
                  ) : (
                    <span className="text-emerald-600 font-semibold text-xs">Al día</span>
                  )}
                </TableCell>
                <TableCell data-testid={`payment-sale-date-${p.id}`}>
                  {p.sale_date ? (
                    <span title={p.sale_date_inferred
                      ? "Fecha de venta inferida de la fecha de pago (matrícula registrada después)"
                      : "Mes en que cuenta la venta"}>
                      {formatDMY(p.sale_date, lang)}
                      {p.sale_date_inferred && <span className="text-amber-600"> *</span>}
                    </span>
                  ) : (
                    <span className="text-slate-300" title="Sin fecha de venta: cuenta por la emisión">—</span>
                  )}
                </TableCell>
                <TableCell>{formatDMY(p.paid_date, lang)}</TableCell>
                <TableCell className="text-right">
                  {p.status !== "paid" && p.status !== "cancelled" && (
                    <Button variant="ghost" size="sm" onClick={() => sendInvoice(p)} className="text-blue-700"
                            data-testid={`send-invoice-${p.id}`}
                            title="Enviarle al estudiante el recordatorio de la factura por pagar">
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {p.status !== "paid" && (
                    <Button variant="ghost" size="sm" onClick={() => openMarkPaid(p)} className="text-green-700" data-testid={`mark-paid-${p.id}`} title="Marcar como pagada">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-payment-${p.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {isSuper && !p.is_annulled && Number(p.amount) > (p.credit_note_total || 0) && (
                    <Button variant="ghost" size="sm" onClick={() => setCreditNoteTarget(p)}
                            className="text-rose-600" data-testid={`credit-note-${p.id}`}
                            title="Emitir nota de crédito: devolverle la parte que no consumió">
                      <ReceiptText className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isSuper && p.status === "paid" && !p.invoice_sent && p.classes_included > 0
                    && (p.classes_consumed || 0) >= p.classes_included && !p.enrollment_orphaned && (
                    <Button variant="ghost" size="sm" onClick={() => setCloseTarget(p)}
                            className="text-amber-700" data-testid={`close-cycle-${p.id}`}
                            title="Cerrar ciclo ahora (sin esperar a medianoche)">
                      <FastForward className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isSuper && p.invoice_sent && p.classes_included > 0 && !p.enrollment_orphaned && (
                    <Button variant="ghost" size="sm" onClick={() => setReopenTarget(p)}
                            className={p.cycle_closed_below_top ? "text-amber-700" : "text-slate-400"}
                            data-testid={`reopen-cycle-${p.id}`}
                            title="Reabrir ciclo: vuelve a estar activo y se cerrará de nuevo, con factura nueva, cuando llegue a su tope">
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {p.enrollment_orphaned && (
                    <Button variant="ghost" size="sm" onClick={() => setKeepTarget(p)}
                            className="text-slate-700" data-testid={`keep-orphan-${p.id}`}
                            title={t("keep_invoice")}>
                      <ShieldCheck className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {p.orphan_kept && (
                    <Button variant="ghost" size="sm" onClick={() => unkeepOrphan(p)}
                            className="text-slate-500" data-testid={`unkeep-orphan-${p.id}`}
                            title={t("unkeep_invoice")}>
                      <Undo2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-red-600" data-testid={`delete-payment-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_payment")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("student")}</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="payment-student-select"><SelectValue placeholder={t("select_student")} /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("program")}</Label>
              <Select value={form.program_id || "none"} onValueChange={(v) => setForm({ ...form, program_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="payment-program-select"><SelectValue placeholder={t("select_program")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (${Number(p.price || 0).toFixed(2)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("amount")}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">$</span>
                  <Input type="number" data-testid="payment-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pl-7 pr-12" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none">USD</span>
                </div>
              </div>
              <div>
                <Label>{t("status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t("pending")}</SelectItem>
                    <SelectItem value="paid">{t("paid")}</SelectItem>
                    <SelectItem value="cancelled">{t("payment_cancelled")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.status === "paid" && (
              <div>
                <Label className="flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5 text-slate-500" />Forma de pago</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METHOD_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t("classes_included")}</Label>
              <Input type="number" data-testid="payment-classes-input" value={form.classes_included} onChange={(e) => setForm({ ...form, classes_included: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("due_date")}</Label>
                <Input type="date" data-testid="payment-due-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("paid_date")}</Label>
                <Input type="date" data-testid="payment-paid-input" value={form.paid_date} onChange={(e) => setForm({ ...form, paid_date: e.target.value })} />
                <p className="text-[11px] text-slate-500 mt-1">Define en qué mes entra la plata</p>
              </div>
            </div>
            <div>
              <Label>Fecha de venta</Label>
              <Input type="date" data-testid="payment-sale-date-input" value={form.sale_date}
                     onChange={(e) => setForm({ ...form, sale_date: e.target.value })} />
              <p className="text-[11px] text-slate-500 mt-1">
                Define en qué mes cuenta la venta. Vacío = el mes en que se emitió
              </p>
            </div>
            <div>
              <Label>{t("notes")}</Label>
              <Textarea data-testid="payment-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button data-testid="save-payment-button" onClick={submit}>{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick "mark as paid" dialog — asks for payment method before saving */}
      <Dialog open={payMethodDialog.open} onOpenChange={(o) => setPayMethodDialog((s) => ({ ...s, open: o }))}>
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-emerald-600" />Marcar como pagada</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <Label>Forma de pago</Label>
              <Select value={payMethodDialog.method} onValueChange={(v) => setPayMethodDialog((s) => ({ ...s, method: v }))}>
                <SelectTrigger data-testid="mark-paid-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHOD_OPTIONS.filter((o) => o.key !== "unspecified").map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>¿Cuándo se pagó?</Label>
              <div className="flex flex-wrap gap-2 mt-1 mb-2">
                {[{ label: "Hoy", days: 0 }, { label: "Ayer", days: 1 }, { label: "Antier", days: 2 }].map((opt) => {
                  const value = daysAgoISO(opt.days);
                  const active = payMethodDialog.paidDate === value;
                  return (
                    <Button key={opt.label} type="button" size="sm"
                            variant={active ? "default" : "outline"}
                            className={active ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                            data-testid={`mark-paid-quick-${opt.days}`}
                            onClick={() => setPayMethodDialog((s) => ({ ...s, paidDate: value }))}>
                      {opt.label}
                    </Button>
                  );
                })}
              </div>
              <Input type="date" max={todayISO()} value={payMethodDialog.paidDate}
                     data-testid="mark-paid-date-input"
                     onChange={(e) => setPayMethodDialog((s) => ({ ...s, paidDate: e.target.value }))} />
              <p className="text-xs text-slate-500 mt-2">
                Los días vencidos se congelan con esta fecha, no con el día en que se registra.
              </p>
              {payMethodDialog.issued && payMethodDialog.paidDate && payMethodDialog.paidDate < payMethodDialog.issued && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2"
                   data-testid="mark-paid-before-issue-warning">
                  Revisá la fecha: es anterior a la emisión de la factura ({formatDMY(payMethodDialog.issued, lang)}). Se puede guardar igual.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMarkPaid}>{t("cancel")}</Button>
            <Button onClick={confirmMarkPaid} data-testid="mark-paid-confirm" className="bg-emerald-600 hover:bg-emerald-700">Marcar pagada</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conservar un cobro sin programa: el ingreso fue real */}
      <Dialog open={!!keepTarget} onOpenChange={(o) => { if (!o) { setKeepTarget(null); setKeepReason(""); } }}>
        <DialogContent data-testid="keep-orphan-dialog">
          <DialogHeader>
            <DialogTitle>{t("keep_invoice_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">{t("keep_invoice_body")}</p>
          <div className="space-y-1">
            <Label>{t("keep_invoice_reason")}</Label>
            <Input value={keepReason} onChange={(e) => setKeepReason(e.target.value)}
                   placeholder={t("keep_invoice_reason_ph")}
                   data-testid="keep-orphan-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setKeepTarget(null); setKeepReason(""); }}>
              {t("cancel")}
            </Button>
            <Button onClick={keepOrphan} data-testid="keep-orphan-confirm">
              <ShieldCheck className="h-4 w-4 mr-1" /> {t("keep_invoice_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Cerrar ciclo ahora: el cierre automático corre a medianoche (hora CR) */}
      <Dialog open={!!closeTarget} onOpenChange={(o) => { if (!o) setCloseTarget(null); }}>
        <DialogContent data-testid="close-cycle-dialog">
          <DialogHeader>
            <DialogTitle>Cerrar ciclo ahora</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-600">
            <p>
              Los ciclos se cierran solos a la <strong>medianoche de Costa Rica</strong>. Con este botón
              cerrás este ciclo de una vez.
            </p>
            {closeTarget && (
              <p className="text-xs">
                Clases tomadas: <strong>{closeTarget.classes_consumed || 0}/{closeTarget.classes_included}</strong>
                {(closeTarget.classes_consumed || 0) < closeTarget.classes_included && (
                  <span className="text-amber-700"> — el ciclo todavía no está completo.</span>
                )}
              </p>
            )}
            <p className="text-xs">
              Se le asegura el consecutivo a esta factura, se crea el cobro del ciclo siguiente con su
              factura y el estudiante recibe el <strong>recordatorio de renovación con su enlace de pago</strong>
              (no se manda la factura adjunta).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseTarget(null)}>{t("cancel")}</Button>
            <Button onClick={closeCycleNow} disabled={closing} data-testid="close-cycle-confirm"
                    className="bg-amber-600 hover:bg-amber-700">
              <FastForward className="h-4 w-4 mr-1" /> Cerrar ciclo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reabrir ciclo: independiente de borrar facturas (decisión 10/09/2026) */}
      <Dialog open={!!reopenTarget} onOpenChange={(o) => { if (!o) setReopenTarget(null); }}>
        <DialogContent data-testid="reopen-cycle-dialog">
          <DialogHeader>
            <DialogTitle>Reabrir ciclo</DialogTitle>
            <DialogDescription>
              El ciclo vuelve a estar activo y se cerrará de nuevo, con su factura, cuando llegue a su tope.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-600">
            <p>
              Este ciclo se cerró y se facturó. Al reabrirlo vuelve a estar activo y a contarse
              desde las asistencias; cuando de verdad llegue a su tope, se cierra otra vez solo y
              emite su factura nueva.
            </p>
            {reopenTarget && (
              <p className="text-xs">
                Clases tomadas hoy: <strong>{reopenTarget.classes_consumed || 0}/{reopenTarget.classes_included}</strong>
                {reopenTarget.cycle_closed_below_top && (
                  <span className="text-amber-700"> — quedó por debajo del tope después de corregir asistencias.</span>
                )}
              </p>
            )}
            <p className="text-xs">
              La factura de <strong>este</strong> ciclo no se toca. Si la factura del ciclo siguiente
              salió antes de tiempo, borrala vos con el basurero: reabrir y borrar son dos decisiones
              aparte.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenTarget(null)}>{t("cancel")}</Button>
            <Button onClick={reopenCycle} disabled={reopening} data-testid="reopen-cycle-confirm"
                    className="bg-amber-600 hover:bg-amber-700">
              <RotateCcw className="h-4 w-4 mr-1" /> Reabrir ciclo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Corrección a mano del contador de ciclos ya cobrados */}
      <Dialog open={suspectsOpen} onOpenChange={(v) => !v && setSuspectsOpen(false)}>
        <DialogContent className="max-w-3xl" data-testid="counter-suspects-dialog">
          <DialogHeader>
            <DialogTitle>Ciclos cobrados con el contador raro</DialogTitle>
            <DialogDescription>
              Revisá cada uno y dejá el contador como de verdad terminó ese ciclo. Solo cambia el
              contador: ni el monto, ni la factura, ni el estado del pago se tocan.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3">
            {visibleSuspects.length === 0 && (
              <p className="text-sm text-slate-500">No quedó ninguno por corregir.</p>
            )}
            {visibleSuspects.map((r) => {
              const draft = fixDraft[r.payment_id] || {};
              const included = draft.included ?? (r.program_classes_included || r.classes_included);
              const consumed = draft.consumed ?? included;
              return (
                <div key={r.payment_id} className="p-3 rounded-lg border border-slate-200 bg-white"
                     data-testid={`counter-suspect-${r.payment_id}`}>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <strong className="text-slate-900">{r.student_name || r.student_id}</strong>
                    <span className="text-slate-500">{r.invoice_number || "sin número"}</span>
                    <span className="text-slate-500">· ${Number(r.amount).toFixed(2)}</span>
                    <span className="text-slate-500">· ciclo {r.cycle_index}</span>
                    <span className="text-red-600 font-mono text-xs">
                      hoy: {r.classes_consumed}/{r.classes_included}
                    </span>
                    {r.enrollment_missing && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300">
                        programa borrado
                      </span>
                    )}
                  </div>
                  {r.program_name && (
                    <p className="text-xs text-slate-500 mt-0.5">{r.program_name}</p>
                  )}
                  <div className="flex flex-wrap items-end gap-3 mt-2">
                    <div>
                      <Label className="text-xs">Clases del ciclo</Label>
                      <Input type="number" min={1} className="w-24 h-8" value={included}
                             data-testid={`counter-included-${r.payment_id}`}
                             onChange={(e) => setFixDraft((d) => ({
                               ...d, [r.payment_id]: { ...draft, included: Number(e.target.value) },
                             }))} />
                    </div>
                    <div>
                      <Label className="text-xs">Clases tomadas</Label>
                      <Input type="number" min={0} className="w-24 h-8" value={consumed}
                             data-testid={`counter-consumed-${r.payment_id}`}
                             onChange={(e) => setFixDraft((d) => ({
                               ...d, [r.payment_id]: { ...draft, consumed: Number(e.target.value) },
                             }))} />
                    </div>
                    <Button size="sm" variant="outline"
                            onClick={() => setFixDraft((d) => ({
                              ...d, [r.payment_id]: { included, consumed: included },
                            }))}
                            data-testid={`counter-complete-${r.payment_id}`}>
                      Ciclo completo
                    </Button>
                    <Button size="sm" onClick={() => fixCounter(r)} disabled={fixing === r.payment_id}
                            data-testid={`counter-fix-${r.payment_id}`}>
                      Guardar {consumed}/{included}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspectsOpen(false)}>{t("close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreditNoteDialog
        payment={creditNoteTarget}
        onOpenChange={(v) => { if (!v) setCreditNoteTarget(null); }}
        onDone={load}
      />
    </div>
  );
}
